import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import TpDialog from '../shared/tp_dialog';
import { fetch_rule_changes, fetch_rules_catalogue, fetch_rules_list } from './api';
import {
  FALLBACK_CATALOGUE,
  catalogue_view,
  change_log_mode,
  change_rules_text,
  change_status_view,
  day_label,
  filter_stages,
  list_view,
  merge_changes,
  refusal,
  stamp_label,
  version_status_view
} from './logic';
import RuleHistoryDialog from './history_dialog';
import RuleEditDialog from './edit_dialog';
import ChangePanel from './change_panel';

/*
 * PROD-7828 R14 P4 — the Rules tab (/teacher-payments/rules-engine): the rules in force on a date,
 * grouped by stage and group, with each rule's history, a value-edit form for rules in force today,
 * the versions scheduled later and the change log. Not month-scoped. Rows keep the server's order
 * (stage, group priority, rule priority) because that order decides which rule wins, so no column sort.
 * The catalogue (labels, allowed month values) loads once; until then, or if it fails, logic.js fallbacks.
 * props: on_error(e, fallback), now(), poll_ms, new_request_id (tests).
 */

const RuleCell = ({ r }) => (
  <span>
    <span className="tp-strong">{r.name}</span>
    {r.sentence ? <div className="tp-hint">{r.sentence}</div> : null}
  </span>
);

const ChangesLog = ({ log, names, on_open, on_retry, on_more, more_loading, more_error }) => {
  const mode = change_log_mode(log.status, log.rows.length);
  const columns = [
    {
      key: 'created',
      label: 'Prepared (IST)',
      hideInCard: true,
      render: (c) => stamp_label(c.created_at_ist)
    },
    {
      key: 'rules',
      label: 'Rules',
      render: (c) => change_rules_text(c, names)
    },
    { key: 'effective', label: 'Takes effect', render: (c) => day_label(c.effective_date) },
    {
      key: 'status',
      label: 'Status',
      render: (c) => {
        const s = change_status_view(c.status);
        return <StatusPill tone={s.tone}>{s.label}</StatusPill>;
      }
    },
    {
      key: 'by',
      label: 'Prepared by',
      render: (c) => (c.requested_by && c.requested_by.name) || '—'
    },
    {
      key: 'note',
      label: 'Why',
      render: (c) => <span style={{ whiteSpace: 'pre-wrap' }}>{c.note}</span>
    },
    {
      key: 'open',
      label: 'Open',
      render: (c) => {
        const label = c.status === 'PREVIEWED' ? 'Preview and save' : 'View';
        return (
          <button
            type="button"
            className="tp-link"
            aria-label={`${label}: ${change_rules_text(c, names) || 'rule change'}, prepared ${stamp_label(c.created_at_ist)}`}
            onClick={() => on_open(c.change_uuid)}
          >
            {label}
          </button>
        );
      }
    }
  ];
  return (
    <section className="tp-card" aria-labelledby="tp-rx-log">
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" id="tp-rx-log">
          Rule changes
        </span>
        <span className="tp-card-hd-sub">
          Newest first. A change not saved yet can be previewed again and saved here.
        </span>
      </div>
      {mode === 'loading' ? (
        <div className="tp-empty" role="status">
          Loading rule changes…
        </div>
      ) : null}
      {mode === 'error' ? <LoadFailed text={log.error} on_retry={on_retry} /> : null}
      {mode === 'table' ? (
        <DataTable
          columns={columns}
          rows={log.rows}
          rowKey={(c) => c.change_uuid}
          cardTitle={(c) => stamp_label(c.created_at_ist)}
          emptyText="No rule changes yet."
        />
      ) : null}
      {mode === 'table' && more_error ? (
        <div className="tp-err" role="alert">
          {more_error}
        </div>
      ) : null}
      {mode === 'table' && log.status === 'ready' && log.next_before ? (
        <button
          type="button"
          className="tp-btn-out"
          style={{ marginTop: 12 }}
          disabled={more_loading}
          onClick={on_more}
        >
          {more_loading ? 'Loading…' : 'Show older changes'}
        </button>
      ) : null}
    </section>
  );
};

const RulesEngineTab = ({ on_error, now = () => new Date(), poll_ms = 3000, new_request_id }) => {
  const [at, setAt] = useState('');
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [list_data, setListData] = useState(null);
  const [catalogue, setCatalogue] = useState(FALLBACK_CATALOGUE);
  const [error, setError] = useState('');
  const [rev, setRev] = useState(0);
  const [filters, setFilters] = useState({ q: '', stage: '', status: '' });
  const [log, setLog] = useState({ status: 'loading', rows: [], next_before: null, error: '' });
  const [more_loading, setMoreLoading] = useState(false);
  const [more_error, setMoreError] = useState('');
  // Bumped per change-log read: only the latest answer lands, so a page-1 reload beats an older page.
  const log_seq = useRef(0);
  const more_busy = useRef(false);
  const [history_key, setHistoryKey] = useState('');
  const [editing, setEditing] = useState(null);
  const [open_change, setOpenChange] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => setRev((r) => r + 1), []);

  useEffect(() => {
    let cancelled = false;
    fetch_rules_catalogue()
      .then((data) => {
        if (!cancelled) setCatalogue(catalogue_view(data));
      })
      .catch(() => {
        // Labels only: the fallbacks still read, and the server checks every value on save.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError('');
    fetch_rules_list(at)
      .then((data) => {
        if (cancelled) return;
        setListData(data);
        setStatus('ready');
      })
      .catch((e) => {
        if (cancelled) return;
        const r = refusal(e, 'Could not load the rules.');
        if (r.permission) on_error(e, r.text);
        setError(r.text);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [at, rev, on_error]);

  const load_changes = useCallback(
    (before) => {
      if (before && more_busy.current) return;
      log_seq.current += 1;
      const s = log_seq.current;
      more_busy.current = !!before;
      setMoreLoading(!!before);
      setMoreError('');
      if (!before) setLog((l) => ({ ...l, status: 'loading', error: '' }));
      fetch_rule_changes(before || 0)
        .then((data) => {
          if (s !== log_seq.current) return;
          setLog((l) => ({
            status: 'ready',
            rows: merge_changes(l.rows, data && data.changes, !!before),
            next_before: (data && data.next_before_id) || null,
            error: ''
          }));
        })
        .catch((e) => {
          if (s !== log_seq.current) return;
          const r = refusal(e, 'Could not load the rule changes.');
          if (r.permission) on_error(e, r.text);
          if (before) setMoreError(r.text);
          else setLog((l) => ({ ...l, status: 'error', error: r.text }));
        })
        .finally(() => {
          if (s !== log_seq.current) return;
          more_busy.current = false;
          setMoreLoading(false);
        });
    },
    [on_error]
  );
  useEffect(() => {
    load_changes(0);
  }, [rev, load_changes]);
  // A late answer after the tab unmounts is dropped.
  useEffect(
    () => () => {
      log_seq.current += 1;
    },
    []
  );

  const view = useMemo(() => list_view(list_data || {}, catalogue), [list_data, catalogue]);
  const stages = useMemo(() => filter_stages(view.stages, filters), [view.stages, filters]);
  const viewing_today = !at || at === view.today;
  let edit_block = '';
  if (!view.can_change)
    edit_block = 'No date is open for a change: every allowed date falls in a closed month.';
  else if (!viewing_today)
    edit_block = 'Changes start from the rules in force today. Clear the date to edit.';

  const columns = [
    { key: 'name', label: 'Rule', hideInCard: true, render: (r) => <RuleCell r={r} /> },
    {
      key: 'teacher',
      label: 'Teachers read',
      render: (r) =>
        r.teacher_title ? (
          <span>
            <span className="tp-strong">{r.teacher_title}</span>
            {r.teacher_why ? <div className="tp-hint">{r.teacher_why}</div> : null}
          </span>
        ) : (
          <span className="tp-hint">Not shown to teachers</span>
        )
    },
    { key: 'effective_text', label: 'In force from' },
    {
      key: 'status',
      label: 'Status',
      render: (r) => {
        const s = version_status_view(r.status);
        return (
          <span>
            <StatusPill tone={s.tone}>{s.label}</StatusPill>
            <div className="tp-hint">Version {r.version_no}</div>
          </span>
        );
      }
    },
    {
      key: 'who',
      label: 'Last changed by',
      render: (r) =>
        r.is_seed
          ? 'Seeded from the old engine'
          : `${r.changed_by}${r.changed_at ? `, ${r.changed_at}` : ''}`
    },
    {
      key: 'actions',
      label: 'Actions',
      hideInCard: true,
      render: (r) => (
        <span className="tp-rx-actions">
          <button
            type="button"
            className="tp-link"
            aria-label={`History of ${r.name}`}
            onClick={() => setHistoryKey(r.rule_key)}
          >
            History
          </button>
          <button
            type="button"
            className="tp-link"
            disabled={!!edit_block || r.status !== 'CURRENT'}
            title={
              edit_block ||
              (r.status !== 'CURRENT' ? 'Only a rule in force can be changed here.' : undefined)
            }
            aria-label={`Change ${r.name}`}
            aria-describedby={edit_block ? 'rx-edit-block' : undefined}
            onClick={() => setEditing(r)}
          >
            Change values
          </button>
        </span>
      )
    }
  ];
  const card_title = (r) => (
    <span>
      <RuleCell r={r} /> {columns[columns.length - 1].render(r)}
    </span>
  );

  return (
    <div>
      <div className="tp-card-hd">
        <span className="tp-card-hd-title">Rules</span>
        <span className="tp-card-hd-sub">
          How every class and month is paid. A change takes effect from a date and never touches a
          closed month.
        </span>
      </div>
      {view.engine_text ? (
        <div className="tp-banner tp-banner-info" role="note">
          {view.engine_text}
        </div>
      ) : null}

      <div className="tp-frow">
        <div className="tp-field" style={{ margin: 0 }}>
          <label htmlFor="rx-at">Rules in force on</label>
          <input
            id="rx-at"
            type="date"
            className="tp-input"
            value={at}
            onChange={(e) => setAt(e.target.value)}
            aria-describedby="rx-at-hint"
          />
          <span id="rx-at-hint" className="tp-hint">
            {at ? (
              <button type="button" className="tp-link" onClick={() => setAt('')}>
                Back to today
              </button>
            ) : (
              'Today (IST)'
            )}
          </span>
        </div>
        <input
          className="tp-input"
          aria-label="Search rules"
          placeholder="Search name, wording or key"
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
        />
        <select
          className="tp-select"
          aria-label="Filter by stage"
          value={filters.stage}
          onChange={(e) => setFilters((f) => ({ ...f, stage: e.target.value }))}
        >
          <option value="">All stages</option>
          {catalogue.stages.map((st) => (
            <option key={st.code} value={st.code}>
              {st.label}
            </option>
          ))}
        </select>
        <select
          className="tp-select"
          aria-label="Filter by status"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
        >
          <option value="">On and off</option>
          <option value="active">On</option>
          <option value="inactive">Switched off</option>
        </select>
      </div>

      {status === 'loading' ? <CustomLoading /> : null}
      {status === 'error' ? <LoadFailed text={error} on_retry={reload} /> : null}
      {status === 'ready' && !view.rule_count ? (
        <div className="tp-empty">No rules were returned.</div>
      ) : null}
      {status === 'ready' && view.rule_count && !stages.length ? (
        <div className="tp-empty">No rule matches these filters.</div>
      ) : null}
      {status === 'ready' && edit_block ? (
        <div id="rx-edit-block" className="tp-banner tp-banner-warn" role="note">
          {edit_block}
        </div>
      ) : null}

      {status === 'ready'
        ? stages.map((s) => (
            <section key={s.stage} aria-labelledby={`tp-rx-stage-${s.stage}`}>
              <h3 className="tp-rx-stage" id={`tp-rx-stage-${s.stage}`}>
                {s.label}
              </h3>
              {s.groups.map((g) => (
                <div key={g.key} className="tp-card">
                  <div className="tp-card-hd">
                    <span className="tp-card-hd-title">{g.name}</span>
                    {g.mode_text ? <span className="tp-card-hd-sub">{g.mode_text}</span> : null}
                  </div>
                  <DataTable
                    columns={columns}
                    rows={g.rules}
                    rowKey={(r) => r.key}
                    cardTitle={card_title}
                  />
                </div>
              ))}
            </section>
          ))
        : null}

      {status === 'ready' && view.scheduled.length ? (
        <section className="tp-card" aria-labelledby="tp-rx-scheduled">
          <div className="tp-card-hd">
            <span className="tp-card-hd-title" id="tp-rx-scheduled">
              Scheduled after {day_label(view.at)}
            </span>
          </div>
          <DataTable
            columns={columns
              .filter((c) => c.key !== 'actions')
              .concat({
                key: 'history',
                label: 'History',
                render: (r) => (
                  <button
                    type="button"
                    className="tp-link"
                    aria-label={`History of ${r.name}`}
                    onClick={() => setHistoryKey(r.rule_key)}
                  >
                    History
                  </button>
                )
              })}
            rows={view.scheduled}
            rowKey={(r) => r.key}
            cardTitle={(r) => <RuleCell r={r} />}
          />
        </section>
      ) : null}

      <ChangesLog
        log={log}
        names={view.names}
        on_open={setOpenChange}
        on_retry={() => load_changes(0)}
        on_more={() => load_changes(log.next_before)}
        more_loading={more_loading}
        more_error={more_error}
      />

      {history_key ? (
        <RuleHistoryDialog
          rule_key={history_key}
          on_close={() => setHistoryKey('')}
          on_error={on_error}
        />
      ) : null}
      {editing ? (
        <RuleEditDialog
          version={editing}
          list={view}
          names={view.names}
          catalogue={catalogue}
          now={now}
          poll_ms={poll_ms}
          new_request_id={new_request_id}
          on_close={() => {
            setEditing(null);
            load_changes(0);
          }}
          on_saved={reload}
          on_rules_changed={() => {
            setEditing(null);
            reload();
          }}
          on_error={on_error}
        />
      ) : null}
      {open_change ? (
        <TpDialog title="Rule change" busy={busy} on_close={() => setOpenChange('')}>
          <div className="tp-modal tp-modal-wide">
            <ChangePanel
              change_uuid={open_change}
              names={view.names}
              catalogue={catalogue}
              now={now}
              poll_ms={poll_ms}
              on_saved={reload}
              on_rules_changed={() => {
                setOpenChange('');
                reload();
              }}
              on_busy={setBusy}
              on_error={on_error}
            />
          </div>
        </TpDialog>
      ) : null}
    </div>
  );
};

export default RulesEngineTab;
